import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Button, Avatar, Spin, Typography, Tag, Divider, theme, Tooltip, Modal } from "antd";
import {
  RobotOutlined,
  UserOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  SearchOutlined,
  ExpandOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DownloadOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import {
  Activity,
  IndianRupee,
  CalendarDays,
  Orbit,
  Trophy,
  Send,
} from "lucide-react";
import type { TextAreaRef } from 'antd/es/input/TextArea';
import axiosInstance from "../../../../utils/axiosInstance";
import {
  addMessage,
  setLoading,
  setMatchedDesign,
  setHasMatched,
  setError,
  selectMessages,
  selectIsLoading,
  MessageItem,
  StructuredResponse,
} from "../../../../redux/testDesignSlice";
import { AppDispatch } from "../../../../redux/store";
import { getTeanantNameFromToken } from "../../../../utils/utils";
import AdrstaIconCircleLogo from "../../../../../src/assets/adrsta_icon_circle.png";

const { Text, Paragraph } = Typography;

// ── Greeting ───────────────────────────────────────────────────────────────
const getGreeting = (): string => {
  const h = new Date().getHours();
  return h < 12 ? "Good Morning" : "Good Evening";
};

// ── Typewriter placeholder texts ───────────────────────────────────────────
const TYPEWRITER_TEXTS = [
  "What if I want to test two different creatives? How should I design this test?",
  "What if I want to measure incremental impact across paid channels?",
  "What if I want to optimize budget allocation across different markets?",
  "What if I want to compare media strategies across multiple markets?",
  "What if I want to design a geo lift experiment for a new channel?",
  "What if I want to measure incrementality for a full-funnel campaign?"
];

// ── Typewriter hook ────────────────────────────────────────────────────────
function useTypewriterPlaceholder(
  texts: string[],
  typingSpeed = 65,
  deletingSpeed = 35,
  pauseMs = 1800
): string {
  const [displayedText, setDisplayedText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    const currentText = texts[textIndex];

    if (!isDeleting && charIndex < currentText.length) {
      const t = setTimeout(() => {
        setDisplayedText(currentText.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, typingSpeed);
      return () => clearTimeout(t);
    }
    if (!isDeleting && charIndex === currentText.length) {
      const t = setTimeout(() => setIsDeleting(true), pauseMs);
      return () => clearTimeout(t);
    }
    if (isDeleting && charIndex > 0) {
      const t = setTimeout(() => {
        setDisplayedText(currentText.slice(0, charIndex - 1));
        setCharIndex((c) => c - 1);
      }, deletingSpeed);
      return () => clearTimeout(t);
    }
    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setTextIndex((i) => (i + 1) % texts.length);
    }
  }, [charIndex, isDeleting, textIndex, texts, typingSpeed, deletingSpeed, pauseMs]);

  return displayedText;
}

// ── Suggestion chips ───────────────────────────────────────────────────────
const SUGGESTION_CHIPS = [
  { label: "Incrementality Measurement", Icon: Activity },
  { label: "Spend Amount", Icon: IndianRupee },
  { label: "Campaign Duration", Icon: CalendarDays },
  { label: "Halo Impact", Icon: Orbit },
  { label: "Channel Ranking", Icon: Trophy },
];

interface TestDesignChatProps {
  onFormSubmit: (data: any) => void;
  jobConfigId: string;
  isViewMode?: boolean;
}

// ── Download helper ──────────────────────────────────────────────────────
const downloadImage = (url: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// ── Image with zoom controls ───────────────────────────────────────────────
const ZoomableImage: React.FC<{ imageUrl: string }> = ({ imageUrl }) => {
  const { token } = theme.useToken();
  const [zoom, setZoom] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const clampZoom = (z: number) => Math.min(3, Math.max(0.5, z));

  return (
    <div
      style={{ position: "relative", borderRadius: token.borderRadius, overflow: "hidden", border: `1px solid ${token.colorBorderSecondary}` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Thumbnail */}
      <div style={{ overflow: "hidden", background: token.colorBgLayout }}>
        <img
          src={imageUrl}
          alt="Test design"
          onClick={() => setModalOpen(true)}
          style={{
            width: "100%",
            maxHeight: 240,
            objectFit: "contain",
            display: "block",
            transform: `scale(${zoom})`,
            transformOrigin: "center center",
            transition: "transform 0.2s ease",
            cursor: "zoom-in",
          }}
        />
      </div>

      {/* Control bar */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          gap: 4,
        }}
      >
        <Tooltip title="Zoom out">
          <Button
            size="small"
            icon={<ZoomOutOutlined />}
            onClick={() => setZoom((z) => clampZoom(z - 0.25))}
            style={{ backgroundColor: "rgba(0,0,0,0.55)", borderColor: "transparent", color: "#fff", backdropFilter: "blur(4px)" }}
          />
        </Tooltip>
        <Tooltip title="Zoom in">
          <Button
            size="small"
            icon={<ZoomInOutlined />}
            onClick={() => setZoom((z) => clampZoom(z + 0.25))}
            style={{ backgroundColor: "rgba(0,0,0,0.55)", borderColor: "transparent", color: "#fff", backdropFilter: "blur(4px)" }}
          />
        </Tooltip>
        <Tooltip title="Full screen">
          <Button
            size="small"
            icon={<FullscreenOutlined />}
            onClick={() => setModalOpen(true)}
            style={{ backgroundColor: "rgba(0,0,0,0.55)", borderColor: "transparent", color: "#fff", backdropFilter: "blur(4px)" }}
          />
        </Tooltip>
        <Tooltip title="Download">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => downloadImage(imageUrl)}
            style={{ backgroundColor: "rgba(0,0,0,0.55)", borderColor: "transparent", color: "#fff", backdropFilter: "blur(4px)" }}
          />
        </Tooltip>
      </div>

      {/* Expand hint — bottom-right, hover only */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            backgroundColor: "rgba(0,0,0,0.55)",
            borderRadius: 4,
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <ExpandOutlined style={{ color: "#fff", fontSize: 13 }} />
        </div>
      )}

      {/* Full-size modal */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        centered
        width="80vw"
        styles={{
          header: {
            padding: "10px 16px",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            marginBottom: 0,
          },
          body: { padding: 0 },
        }}
      >
        <img
          src={imageUrl}
          alt="Test design (full screen)"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </Modal>
    </div>
  );
};

// ── Structured response card ───────────────────────────────────────────────
const StructuredCard: React.FC<{ s: StructuredResponse; imageUrl: string | null }> = ({ s, imageUrl }) => {
  const { token } = theme.useToken();
  const isRealMatch = !!s.test_design;

  if (!isRealMatch) {
    return (
      <div
        style={{
          padding: "14px 16px",
          borderRadius: `${token.borderRadiusSM}px 18px 18px 18px`,
          backgroundColor: token.colorBgContainer,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadowTertiary,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <SearchOutlined style={{ color: token.colorTextSecondary, fontSize: token.fontSize, marginTop: 2, flexShrink: 0 }} />
          <Text style={{ fontSize: token.fontSize, color: token.colorText, lineHeight: token.lineHeight }}>
            {s.headline}
          </Text>
        </div>
        {s.key_insight && (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: token.borderRadius,
              backgroundColor: token.colorBgLayout,
              border: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, lineHeight: token.lineHeight }}>
              💡 {s.key_insight}
            </Text>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: `${token.borderRadiusSM}px 18px 18px 18px`,
        backgroundColor: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: token.boxShadowTertiary,
        overflow: "hidden",
      }}
    >
      {/* Headline */}
      <div style={{ padding: "14px 16px 0" }}>
        <Text strong style={{ fontSize: token.fontSizeLG, color: token.colorText, lineHeight: token.lineHeightLG, display: "block" }}>
          {s.headline}
        </Text>
      </div>

      {/* Test design badge + method */}
      <div style={{ padding: "8px 16px 0", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
        <Tag color="blue" style={{ margin: 0, fontSize: token.fontSizeSM, fontWeight: 600 }}>{s.test_design}</Tag>
        {s.measurement_method && s.measurement_method !== s.test_design && (
          <Tag style={{ margin: 0, fontSize: token.fontSizeSM }}>{s.measurement_method}</Tag>
        )}
        {s.duration && (
          <Tag icon={<ClockCircleOutlined />} style={{ margin: 0, fontSize: token.fontSizeSM }}>
            {s.duration}
          </Tag>
        )}
        {s.minimum_spend && (
          <Tag style={{ margin: 0, fontSize: token.fontSizeSM }}>Min: {s.minimum_spend}</Tag>
        )}
      </div>

      <Divider style={{ margin: "10px 0" }} />

      {/* Hypothesis */}
      {s.hypothesis && (
        <div style={{ padding: "0 16px 10px" }}>
          <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, fontStyle: "italic", lineHeight: token.lineHeight }}>
            {s.hypothesis}
          </Text>
        </div>
      )}

      {/* How it works */}
      {s.how_it_works && (
        <div style={{ padding: "0 16px 12px" }}>
          <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, display: "block", marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            How It Works
          </Text>
          <Paragraph style={{ margin: 0, fontSize: token.fontSize, color: token.colorText, lineHeight: token.lineHeight }}>
            {s.how_it_works}
          </Paragraph>
        </div>
      )}

      {/* Slide image with zoom */}
      {imageUrl && (
        <div style={{ margin: "0 16px 12px" }}>
          <ZoomableImage imageUrl={imageUrl} />
        </div>
      )}

      {/* Key insight */}
      {s.key_insight && (
        <div style={{ margin: "0 16px 12px", padding: "10px 12px", borderRadius: token.borderRadius, backgroundColor: token.colorInfoBg, border: `1px solid ${token.colorInfoBorder}` }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <InfoCircleOutlined style={{ color: token.colorInfo, fontSize: token.fontSize, marginTop: 2, flexShrink: 0 }} />
            <Text style={{ fontSize: token.fontSizeSM, color: token.colorInfoText, lineHeight: token.lineHeight }}>
              <Text strong style={{ color: token.colorInfoText, fontSize: token.fontSizeSM }}>Key Insight: </Text>
              {s.key_insight}
            </Text>
          </div>
        </div>
      )}

      {/* Next steps */}
      {s.next_steps && (
        <div style={{ padding: "0 16px 14px", display: "flex", alignItems: "flex-start", gap: 6 }}>
          <ThunderboltOutlined style={{ color: token.colorPrimary, fontSize: token.fontSizeSM, marginTop: 3, flexShrink: 0 }} />
          <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, lineHeight: token.lineHeight }}>
            <Text strong style={{ color: token.colorText, fontSize: token.fontSizeSM }}>Next: </Text>
            {s.next_steps}
          </Text>
        </div>
      )}
    </div>
  );
};

// ── User bubble ────────────────────────────────────────────────────────────
const UserBubble: React.FC<{ message: MessageItem }> = ({ message }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", gap: 8, marginBottom: 16 }}>
      <div style={{ maxWidth: "68%", padding: "10px 16px", borderRadius: "18px 18px 4px 18px", background: token.colorPrimary, boxShadow: token.boxShadowSecondary }}>
        <span style={{ color: "#fff", fontSize: token.fontSize, lineHeight: String(token.lineHeight), fontFamily: "inherit" }}>
          {message.content}
        </span>
      </div>
      <Avatar size={30} icon={<UserOutlined />} style={{ backgroundColor: token.colorPrimaryBorder, flexShrink: 0 }} />
    </div>
  );
};

// ── AI bubble ──────────────────────────────────────────────────────────────
const AIBubble: React.FC<{ message: MessageItem }> = ({ message }) => {
  const { token } = theme.useToken();

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
      <Avatar size={30} icon={<RobotOutlined />} style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }} />
      <div style={{ maxWidth: "85%", flex: 1 }}>
        {message.structured ? (
          <StructuredCard s={message.structured} imageUrl={message.image_url} />
        ) : (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: `${token.borderRadiusSM}px 18px 18px 18px`,
              backgroundColor: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              boxShadow: token.boxShadowTertiary,
            }}
          >
            <Paragraph style={{ margin: 0, fontSize: token.fontSize, lineHeight: token.lineHeight, color: token.colorText, whiteSpace: "pre-wrap" }}>
              {message.content}
            </Paragraph>
            {message.image_url && (
              <div style={{ marginTop: 10 }}>
                <ZoomableImage imageUrl={message.image_url} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Loading bubble ─────────────────────────────────────────────────────────
const LoadingBubble: React.FC = () => {
  const { token } = theme.useToken();
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
      <Avatar size={30} icon={<RobotOutlined />} style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }} />
      <div style={{ padding: "12px 18px", borderRadius: `${token.borderRadiusSM}px 18px 18px 18px`, backgroundColor: token.colorBgContainer, border: `1px solid ${token.colorBorderSecondary}` }}>
        <Spin size="small" />
      </div>
    </div>
  );
};

// ── Typography constants ───────────────────────────────────────────────────
const FONT_STACK = "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ── Main component ─────────────────────────────────────────────────────────
const TestDesignChat: React.FC<TestDesignChatProps> = ({ onFormSubmit, jobConfigId, isViewMode = false }) => {
  const { token } = theme.useToken();
  const dispatch = useDispatch<AppDispatch>();

  const messages = useSelector(selectMessages);
  const isLoading = useSelector(selectIsLoading);

  const [inputValue, setInputValue] = useState("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<TextAreaRef>(null);

  const greeting = getGreeting();
  const tenantName = getTeanantNameFromToken();

  const isEmptyChat = messages.length === 0;

  // Typewriter placeholder — active only on the landing screen
  const typewriterPlaceholder = useTypewriterPlaceholder(TYPEWRITER_TEXTS);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  const handleSubmit = async (overrideQuestion?: string) => {
    const question = (overrideQuestion ?? inputValue).trim();
    if (!question || isLoading) return;

    dispatch(addMessage({
      role: "user",
      content: question,
      image_url: null,
      design: null,
      confidence: null,
      reason: null,
      follow_up: null,
      structured: null,
      timestamp: new Date().toISOString(),
    }));
    setInputValue("");
    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const { data } = await axiosInstance.post("/api/test-design/match", {
        question,
        job_config_id: jobConfigId,
      });

      dispatch(addMessage({
        role: "assistant",
        content: data.summary ?? "",
        image_url: data.image_url ?? null,
        design: data.design ?? null,
        confidence: data.confidence ?? null,
        reason: data.reason ?? null,
        follow_up: data.follow_up ?? null,
        structured: data.structured ?? null,
        timestamp: new Date().toISOString(),
      }));

      if (data.design && data.image_url) {
        dispatch(setMatchedDesign(data));
        dispatch(setHasMatched(true));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? "Something went wrong. Please try again.";
      dispatch(setError(msg));
      dispatch(addMessage({
        role: "assistant",
        content: msg,
        image_url: null,
        design: null,
        confidence: null,
        reason: null,
        follow_up: null,
        structured: null,
        timestamp: new Date().toISOString(),
      }));
    } finally {
      dispatch(setLoading(false));
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSend = !!inputValue.trim() && !isLoading;

  // ── Input box (Claude-style, send button inside) ───────────────────────
  const inputBar = (isLanding: boolean) => (
    <div
      className="td-input-container"
      style={{
        position: "relative",
        width: "100%",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        boxShadow: isLanding
          ? "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)"
          : "0 1px 3px rgba(0,0,0,0.04)",
        backgroundColor: "#ffffff",
      }}
    >
      <Input.TextArea
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={typewriterPlaceholder}
        autoSize={{ minRows: isLanding ? 4 : 2, maxRows: 8 }}
        disabled={isLoading}
        className="td-chat-input"
        style={{
          resize: "none",
          fontFamily: FONT_STACK,
          fontSize: 15,
          lineHeight: 1.6,
          border: "none",
          boxShadow: "none",
          padding: isLanding ? "20px 20px 60px 20px" : "14px 16px 52px 16px",
          color: "#1e293b",
          backgroundColor: "transparent",
          borderRadius: 12,
        }}
      />
      {/* Send button — inside box, bottom-right */}
      <div style={{ position: "absolute", bottom: 12, right: 12 }}>
        <button
          onClick={() => handleSubmit()}
          disabled={!canSend}
          aria-label="Send message"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "none",
            backgroundColor: canSend ? "#0055b8" : "#e2e8f0",
            color: canSend ? "#ffffff" : "#94a3b8",
            cursor: canSend ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s ease, color 0.2s ease",
            outline: "none",
            flexShrink: 0,
          }}
        >
          <Send size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Scoped styles */}
      <style>{`
        .td-chat-input {
          border: none !important;
          box-shadow: none !important;
        }
        .td-chat-input:focus,
        .td-chat-input:focus-within {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .td-chat-input textarea::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }
        .td-chat-input .ant-input {
          font-family: ${FONT_STACK};
        }
        .td-input-container:focus-within {
          border-color: #cbd5e1 !important;
          box-shadow: 0 0 0 3px rgba(148,163,184,0.18) !important;
        }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: FONT_STACK }}>

        {isEmptyChat ? (
          /* ── Landing screen ── */
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 32px 40px",
              textAlign: "center",
            }}
          >
            {/* Logo + Greeting — one horizontal row */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
              }}
            >
              {/* Adrsta logo */}
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: "50%",
                  backgroundColor: "#0055b8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: 20,
                    fontWeight: 600,
                    lineHeight: 1,
                    fontFamily: FONT_STACK,
                    letterSpacing: "-0.3px",
                  }}
                >
                  <img src={AdrstaIconCircleLogo} ></img>
                </span>
              </div>

              {/* Greeting text */}
              <h1
                style={{
                  margin: 0,
                  fontFamily: FONT_STACK,
                  fontSize: 30,
                  fontWeight: 500,
                  color: "#0f172a",
                  lineHeight: 1.2,
                  letterSpacing: "-0.4px",
                  whiteSpace: "nowrap",
                }}
              >
                {greeting}
                {tenantName
                  ? <>, <span style={{ color: "#0f172a" }}>{tenantName}</span>!</>
                  : "!"}
              </h1>
            </div>

            {/* Subtitle */}
            <p
              style={{
                margin: "0 0 0",
                fontFamily: FONT_STACK,
                fontSize: 16,
                fontWeight: 400,
                color: "#64748b",
                lineHeight: 1.65,
                maxWidth: 420,
                letterSpacing: "0.01em",
              }}
            >
              What do you want to experiment or measure today?
            </p>

            {/* Claude-style input box */}
            <div style={{ width: "100%", maxWidth: 780, marginTop: 36 }}>
              {inputBar(true)}
            </div>

            {/* Decorative suggestion chips with icons */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "center",
                marginTop: 20,
              }}
            >
              {SUGGESTION_CHIPS.map(({ label, Icon }) => (
                <span
                  key={label}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 12px",
                    borderRadius: 20,
                    border: "1px solid #e8ecf0",
                    backgroundColor: "transparent",
                    fontSize: 12,
                    color: "#94a3b8",
                    fontWeight: 400,
                    userSelect: "none",
                    cursor: "default",
                    letterSpacing: "0.02em",
                    fontFamily: FONT_STACK,
                  }}
                >
                  <Icon size={12} strokeWidth={1.5} />
                  {label}
                </span>
              ))}
            </div>
          </div>

        ) : (
          /* ── Chat messages ── */
          <>
            {isViewMode && (
              <div style={{ padding: "8px 16px", borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  This test design was selected during study creation.
                </Text>
              </div>
            )}
            <div
              ref={messagesContainerRef}
              style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: `${token.paddingMD}px` }}
            >
              {messages.map((msg, idx) =>
                msg.role === "user"
                  ? <UserBubble key={idx} message={msg} />
                  : <AIBubble key={idx} message={msg} />
              )}
              {isLoading && <LoadingBubble />}
            </div>

            {!isViewMode && (
              /* Input pinned to bottom */
              <div
                style={{
                  padding: `${token.paddingSM}px ${token.paddingMD}px`,
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                  backgroundColor: token.colorBgContainer,
                }}
              >
                {inputBar(false)}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TestDesignChat;
